import { captureException, logger, setTag } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type AccessDenialReason, parseAccessDenialReason } from '@workspace/shared';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  getAuthMe,
  postAuthConnectMailbox,
  postAuthConnectMailboxTicket,
  postAuthConnectMailboxTicketStatus,
} from '@/api/generated/auth/auth';
import type { GetAuthMe200UserAnyOf } from '@/api/generated/lucioAPI.schemas';
import { acquireMsalTokenInteractive } from '@/config/auth/msal';
import {
  acquireMailboxTokenViaDialog,
  isOfficeDialogAvailable,
  MailboxConnectCancelledError,
  MailboxConnectDialogLostError,
  MailboxConnectFailedError,
} from '@/config/auth/msalConnectDialog';
import {
  acquireMailboxTokenViaNaa,
  isExpectedNaaFallback,
  isNaaSupported,
  isNaaUserCancellation,
} from '@/config/auth/naa';
import { DialogBlockedError } from '@/config/auth/officeDialogManager';
import i18n from '@/config/i18n/i18n';
import { getUserEmail } from '@/config/office/office';
import { queryKeys } from '@/config/query/queryKeys';
import { getStoredDeployment } from '@/utils/apiBaseUrl';
import { ServiceUnavailableError } from '@/utils/errors';
import { ROUTES } from '@/utils/routeUtils';

/**
 * How long to keep asking the backend how the sign-in ended after the host
 * stopped tracking the dialog. Matches the dialog's own timeout: a user the
 * host lost sight of has exactly as long to finish as one it can still see,
 * and anything shorter would call a sign-in cancelled while it is completing.
 */
const VERIFY_TIMEOUT_MS = 300_000;
const VERIFY_INTERVAL_MS = 2000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The dialog's completion attempt was refused for a reason the user can act
 * on. `denialReason` is set when the refusal was an org-entitlement denial,
 * which routes to /forbidden exactly as the API mutator does for a 403 on the
 * fast path — the lost-handle path must not degrade a denial into a toast.
 */
class MailboxConnectRejectedError extends Error {
  readonly denialReason: AccessDenialReason | null;

  constructor(message: string, denialReason: AccessDenialReason | null = null) {
    super(message);
    this.name = 'MailboxConnectRejectedError';
    this.denialReason = denialReason;
  }
}

/**
 * Reads the connected user back after a `linked` outcome. Null on a transient
 * failure or an unpopulated user — the caller re-reads until its deadline.
 */
async function readConnectedUser(): Promise<GetAuthMe200UserAnyOf | null> {
  try {
    const me = await getAuthMe();
    return me.user ?? null;
  } catch {
    return null;
  }
}

/**
 * Waits for the dialog's own call to /auth/connect-mailbox-via-ticket to land,
 * which is how a link completes when the window handle was severed.
 *
 * Polls the ticket's recorded outcome rather than /auth/me, so a failure is
 * reported as itself: without this a 409 linking conflict or a 403 denial
 * raised on the dialog's call is indistinguishable from silence, and the user
 * is told they cancelled a sign-in that was actually refused for a reason
 * worth reading.
 *
 * Returns the connected user, or null when nothing was ever recorded — only
 * then was it really a cancellation.
 */
async function waitForTicketOutcome(ticket: string): Promise<GetAuthMe200UserAnyOf | null> {
  const deadline = Date.now() + VERIFY_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await delay(VERIFY_INTERVAL_MS);

    let status: Awaited<ReturnType<typeof postAuthConnectMailboxTicketStatus>>;
    try {
      status = await postAuthConnectMailboxTicketStatus({ ticket });
    } catch {
      // Transient failure — keep waiting, the deadline still bounds this.
      continue;
    }

    if (status.status === 'pending') {
      continue;
    }

    if (status.status === 'linked') {
      // The outcome says linked; read the user back the same way every other
      // caller does rather than trusting a second source of truth — and
      // tolerate the same transient flakiness as the status poll above. The
      // recorded outcome is terminal, so re-reading until the deadline can
      // only succeed late, never lie.
      const user = await readConnectedUser();
      if (user) {
        return user;
      }
      continue;
    }

    throw new MailboxConnectRejectedError(
      status.message ??
        i18n.t(
          status.status === 'failed'
            ? 'connectMailbox.error.default'
            : 'connectMailbox.error.refused',
        ),
      // Only a denial carries a reason, and it is what routes the user to the
      // page that explains it. Discarding it here would turn "your access has
      // expired" into an unexplained refusal.
      status.status === 'denied' ? parseAccessDenialReason(status.reason) : null,
    );
  }

  return null;
}

/** No dialog API in this host: acquire by popup and link from here. */
async function linkViaPopup(
  loginHint: string | undefined,
  replaceExisting: boolean,
  noTokenMessage: string,
): Promise<GetAuthMe200UserAnyOf> {
  const { accessToken } = await acquireMsalTokenInteractive(loginHint);
  if (!accessToken) {
    throw new Error(noTokenMessage);
  }
  const connected = await postAuthConnectMailbox({
    identityToken: accessToken,
    ...(replaceExisting ? { replaceExisting: true } : {}),
  });
  return connected.user;
}

/**
 * The host brokers the token for the account already signed into Outlook, so
 * there is no second window to lose. Returns null when this host can't broker
 * or the attempt failed for a reason worth falling back on; throws only when
 * the user themselves declined.
 */
async function tryLinkViaNaa(
  loginHint: string | undefined,
  replaceExisting: boolean,
): Promise<GetAuthMe200UserAnyOf | null> {
  if (!isNaaSupported()) {
    return null;
  }

  let accessToken: string;
  try {
    ({ accessToken } = await acquireMailboxTokenViaNaa(loginHint));
  } catch (error) {
    if (isNaaUserCancellation(error)) {
      // The host owns the brokered window, so a cancellation here is real —
      // opening a second sign-in surface would just fight the user.
      throw new MailboxConnectCancelledError();
    }

    // Anything else: the dialog flow is fully supported, so fall back rather
    // than fail. Report the unexpected ones — a host that advertises NAA and
    // then can't deliver is worth knowing about.
    logger.warn('Connect mailbox: NAA acquisition failed, falling back to the dialog');
    if (!isExpectedNaaFallback(error)) {
      captureException(error, { tags: { flow: 'connect-mailbox', stage: 'naa' } });
    }
    return null;
  }

  const connected = await postAuthConnectMailbox({
    identityToken: accessToken,
    ...(replaceExisting ? { replaceExisting: true } : {}),
  });
  return connected.user;
}

/**
 * The dialog completes the link itself using a single-use ticket, so a severed
 * window handle can no longer strand a sign-in the user actually completed.
 */
async function linkViaDialog(
  loginHint: string | undefined,
  replaceExisting: boolean,
): Promise<GetAuthMe200UserAnyOf> {
  const { ticket } = await postAuthConnectMailboxTicket({ replaceExisting });

  // The name, not a resolved URL — see MailboxDialogRequest. Absent means no
  // deployment claim, which every API call would fail on anyway.
  const deployment = getStoredDeployment();
  if (!deployment) {
    throw new ServiceUnavailableError();
  }

  let accessToken: string;
  try {
    ({ accessToken } = await acquireMailboxTokenViaDialog({ deployment, loginHint, ticket }));
  } catch (error) {
    if (!(error instanceof MailboxConnectDialogLostError)) {
      throw error;
    }
    // The host lost sight of the window. That is not the same as the user
    // cancelling — they may still be signing in. Ask the backend.
    logger.warn('Connect mailbox: window handle lost, verifying with the backend');
    const user = await waitForTicketOutcome(ticket);
    if (!user) {
      throw new MailboxConnectCancelledError();
    }
    logger.info('Connect mailbox: link completed by the dialog after the handle was lost');
    return user;
  }

  // The dialog relayed a token, so its own call has almost certainly linked the
  // mailbox already. Confirm before assuming it.
  const me = await getAuthMe();
  if (me.mailboxConnected && me.graphTokenActive && me.user) {
    return me.user;
  }

  // Relay arrived but the dialog's call didn't land (its network died with the
  // window, say). We hold a valid token — finish it here.
  const connected = await postAuthConnectMailbox({
    identityToken: accessToken,
    ...(replaceExisting ? { replaceExisting: true } : {}),
  });
  return connected.user;
}

/**
 * What a failed attempt means for the user. `expected` outcomes are things the
 * user did or must fix — logged so they stay countable, never captured as
 * defects. `forbidden` routes to the page explaining the denial. `defect` is
 * everything else, and is the single capture point for this flow.
 */
type ConnectErrorHandling =
  | { kind: 'expected'; message: string }
  | { denialReason: AccessDenialReason; kind: 'forbidden' }
  | { kind: 'defect'; message: string };

function classifyConnectError(error: Error, t: (key: string) => string): ConnectErrorHandling {
  if (error instanceof MailboxConnectCancelledError) {
    // Not a defect, but it must leave a trace: this is the one outcome a user
    // reports as "it failed" while Sentry shows nothing at all.
    logger.warn('Connect mailbox: attempt ended as cancelled');
    return { kind: 'expected', message: t('connectMailbox.error.cancelled') };
  }

  if (error instanceof MailboxConnectRejectedError) {
    // The backend refused the dialog's own call for a reason the user can act
    // on. It reached us through the ticket outcome rather than a response, but
    // it is the same refusal the fast path would have shown — so it gets the
    // same treatment, including routing to /forbidden for a denial.
    logger.warn('Connect mailbox: refused by the backend during the dialog completion', {
      denialReason: error.denialReason ?? 'none',
    });
    return error.denialReason
      ? { denialReason: error.denialReason, kind: 'forbidden' }
      : { kind: 'expected', message: error.message };
  }

  if (error instanceof DialogBlockedError) {
    // Outlook's "allow dialog" prompt was ignored, or the browser blocks
    // pop-ups — fixed by the user, not by retrying.
    logger.warn('Connect mailbox: dialog blocked before it opened', {
      officeDialogErrorCode: error.code,
    });
    return { kind: 'expected', message: t('connectMailbox.error.dialogBlocked') };
  }

  return { kind: 'defect', message: error.message || t('connectMailbox.error.default') };
}

/**
 * Single capture point for every genuine failure of this flow — Office dialog
 * failures, timeouts, provider errors relayed from the dialog window, and
 * /auth/connect-mailbox rejections alike. Without it the whole dialog path
 * produced no Sentry event at all, which is what made the federated COOP
 * incident invisible.
 */
function reportConnectDefect(error: Error, replaceExisting: boolean): void {
  // oxlint-disable-next-line no-console
  console.error('Connect mailbox failed:', error);
  const dialogErrorCode =
    error instanceof MailboxConnectFailedError ? error.officeDialogErrorCode : undefined;
  captureException(error, {
    // Tags, not extra: Sentry only indexes tags, so these are the fields you
    // can actually search on (`officeDialogErrorCode:12004`).
    tags: {
      flow: 'connect-mailbox',
      officeDialogErrorCode: dialogErrorCode === undefined ? 'none' : String(dialogErrorCode),
      replaceExisting: String(replaceExisting),
      stage: error instanceof MailboxConnectFailedError ? error.stage : 'backend',
    },
  });
}

interface UseConnectMailboxOptions {
  /** Called after the mailbox is connected and caches are refreshed. */
  onSuccess?: () => void;
  /**
   * Switch mailboxes: unlink the caller's previously connected mailbox before
   * linking this one (self-serve only — cross-account conflicts still 409).
   */
  replaceExisting?: boolean;
}

/**
 * Connects (or reconnects) the mailbox, then seeds the user cache and enters
 * the app. Three acquisition paths, in order of preference:
 *
 *  1. **NAA** — the Office host brokers a token for the account already signed
 *     into Outlook. Silent for most users, and immune to the severed-handle
 *     failure because no second window exists.
 *  2. **Office dialog + ticket** — for hosts without NAA. The dialog links the
 *     mailbox itself, so the result survives a lost window handle.
 *  3. **MSAL popup** — only outside Office hosts (the taskpane opened directly
 *     in a browser during development), where no dialog API exists.
 *
 * Every path ends at /auth/connect-mailbox with the same identity token.
 */
export function useConnectMailbox(
  onError: (message: string) => void,
  options: UseConnectMailboxOptions = {},
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async () => {
      const loginHint = getUserEmail() ?? undefined;
      const replaceExisting = Boolean(options.replaceExisting);

      const viaNaa = await tryLinkViaNaa(loginHint, replaceExisting);
      if (viaNaa) {
        setTag('connectMailboxPath', 'naa');
        return viaNaa;
      }

      if (!isOfficeDialogAvailable()) {
        setTag('connectMailboxPath', 'popup');
        return linkViaPopup(loginHint, replaceExisting, t('connectMailbox.error.default'));
      }

      setTag('connectMailboxPath', 'dialog');
      return linkViaDialog(loginHint, replaceExisting);
    },
    onError: (error) => {
      const handling = classifyConnectError(error, t);

      if (handling.kind === 'forbidden') {
        navigate(`${ROUTES.FORBIDDEN}?reason=${handling.denialReason}`, { replace: true });
        return;
      }

      if (handling.kind === 'defect') {
        reportConnectDefect(error, Boolean(options.replaceExisting));
      }

      onError(handling.message);
    },
    onSuccess: async (user) => {
      queryClient.setQueryData(queryKeys.auth.user(), user);
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });
      options.onSuccess?.();
      navigate(ROUTES.HOME, { replace: true });
    },
    // Interactive sign-in must never be retried automatically. The queryClient
    // default (retry: 1) re-ran this whole mutation ~1s after any rejection,
    // silently opening a SECOND Office dialog with no user gesture — and that
    // second open closes the first, which comes back as a 12006 "cancelled".
    // Matches useAuthDialog, which already opts out for the same reason.
    retry: false,
  });
}
